import * as ApiCalls from "../../apiCalls";
import * as IncidentInvestigationConstants from "../../illicitDischarge/incidentInvestigation/incidentInvestigationConstants";
import { getTrendIconClass } from "./utils";
import { DashboardConfig, InsightData } from "../../types/dashboardConfig";
import { FilterSelect } from "./filters/FilterSelect";
import * as InsightWidgetFunctions from "../../general/widgets/insightWidgetFunctions";

export const iddeDashboardConfig: DashboardConfig = {
  dashboardType: "idde",
  getInsightDataFunction: ApiCalls.getIddeIncidentInsight,
  getReportingYearsFunction: async (dashboardType: string) => {
    const reports = await ApiCalls.getAnnualReports(dashboardType);
    return reports;
  },
  getFilterOptionsFunction: (dashboardType: string) => {
    const options = IncidentInvestigationConstants.getFilterIncidentTypeOptions();
    options.unshift({ name: "All Types", value: "total" });
    return options;
  },
  widgetConfigs: [
    {
      type: "numberCard",
      props: {
        title: "Total # of Incidents",
        unit: "Incidents",
      },
      getData: (insightData: InsightData) => {
        const focusDataNumber = insightData.focus.incidentCount;
        const comparisonDataNumber = insightData.comparison.incidentCount;
        const focusSubtitle = String(insightData.focus.year);
        const comparisonSubtitle = String(insightData.comparison.year);
        const trendIconClass = getTrendIconClass(focusDataNumber, comparisonDataNumber);

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusSubtitle,
          comparisonSubtitle,
          trendIconClass,
        };
      },
    },
    {
      type: "numberCard",
      props: {
        title: "Total Investigation Time",
        decimalPlaces: 1,
      },
      getData: (insightData: InsightData) => {
        const focusHours = insightData.focus.investigationTime;
        const comparisonHours = insightData.comparison.investigationTime;

        const focusDataNumber = InsightWidgetFunctions.convertTime(focusHours);
        const comparisonDataNumber = InsightWidgetFunctions.convertTime(comparisonHours);

        const focusDataUnit = InsightWidgetFunctions.convertTimeUnit(focusHours);
        const comparisonDataUnit = InsightWidgetFunctions.convertTimeUnit(comparisonHours);

        const focusSubtitle = String(insightData.focus.year);
        const comparisonSubtitle = String(insightData.comparison.year);
        const trendIconClass = getTrendIconClass(focusHours, comparisonHours);

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusDataUnit,
          comparisonDataUnit,
          focusSubtitle,
          comparisonSubtitle,
          trendIconClass,
        };
      },
    },
    {
      type: "numberCard",
      props: {
        title: "Average Time Open",
        decimalPlaces: 1,
      },
      getData: (insightData: InsightData) => {
        const focusHours = insightData.focus.avgTimeOpen;
        const comparisonHours = insightData.comparison.avgTimeOpen;

        const focusDataNumber = InsightWidgetFunctions.convertTime(focusHours);
        const comparisonDataNumber = InsightWidgetFunctions.convertTime(comparisonHours);

        const focusDataUnit = InsightWidgetFunctions.convertTimeUnit(focusHours);
        const comparisonDataUnit = InsightWidgetFunctions.convertTimeUnit(comparisonHours);

        const focusSubtitle = String(insightData.focus.year);
        const comparisonSubtitle = String(insightData.comparison.year);
        const trendIconClass = getTrendIconClass(focusHours, comparisonHours);

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusDataUnit,
          comparisonDataUnit,
          focusSubtitle,
          comparisonSubtitle,
          trendIconClass,
        };
      },
    },
    {
      type: "numberCard",
      props: {
        title: "Maximum Time Open",
        decimalPlaces: 1,
      },
      getData: (insightData: InsightData) => {
        const focusHours = insightData.focus.maxTimeOpen;
        const comparisonHours = insightData.comparison.maxTimeOpen;

        const focusDataNumber = InsightWidgetFunctions.convertTime(focusHours);
        const comparisonDataNumber = InsightWidgetFunctions.convertTime(comparisonHours);

        const focusDataUnit = InsightWidgetFunctions.convertTimeUnit(focusHours);
        const comparisonDataUnit = InsightWidgetFunctions.convertTimeUnit(comparisonHours);

        const focusSubtitle = String(insightData.focus.year);
        const comparisonSubtitle = String(insightData.comparison.year);
        const trendIconClass = getTrendIconClass(focusHours, comparisonHours);

        return {
          focusDataNumber,
          comparisonDataNumber,
          focusDataUnit,
          comparisonDataUnit,
          focusSubtitle,
          comparisonSubtitle,
          trendIconClass,
        };
      },
    },
    {
      type: "comparisonAreaChart",
      props: {
        title: "Monthly Incidents",
        descriptionTerm: "cumulative monthly incidents",
      },
      getData: (insightData: InsightData) => ({
        focusData: insightData.focus.monthlyIncidentCount,
        comparisonData: insightData.comparison.monthlyIncidentCount,
        focusYear: insightData.focus.year,
        comparisonYear: insightData.comparison.year,
      }),
    },
    {
      type: "breakdownBarChart",
      props: {
        title: "Incidents by Type",
        descriptionTerm: "incidents",
      },
      getData: (insightData: InsightData) => ({
        focusData: insightData.focus.incidentCountByIncidentType,
        comparisonData: insightData.comparison.incidentCountByIncidentType,
        focusYear: insightData.focus.year,
        comparisonYear: insightData.comparison.year,
      }),
    },
    {
      type: "topList",
      props: {
        title: "Top 5 Incident Activity/Material Types",
        descriptionTerm: "activities",
        itemLabel: "Activity",
        keyToDisplayNameFunction: (key: string): string => {
          const allOptions = IncidentInvestigationConstants.getIllicitActivityOptions();
          const matchedItem = allOptions.find(
            (item: { name: string; value: string }) => item.value === key,
          );
          return matchedItem?.name || key;
        },
      },
      getData: (insightData: InsightData) => ({
        focusData: insightData.focus.topActivity,
        comparisonData: insightData.comparison.topActivity,
        focusYear: insightData.focus.year,
        comparisonYear: insightData.comparison.year,
      }),
    },
  ],
  configurableFilters: [
    {
      component: FilterSelect,
      props: {
        label: "Incident Type",
        placeholder: "Select an incident type",
      },
      stateKey: "incidentType",
      initialValue: "total",
    },
  ],
};
